/**
 * `DeckStudySummary` — one small, derived row per deck that answers every
 * Home-screen question about the deck (total, new, due at any cutoff, labels,
 * mastery, last studied, first-studied-today) WITHOUT the card bodies.
 *
 * It is a pure function of the deck's cards (`summarizeDeckCards`), so it can
 * be recomputed from a resident bucket, or from the rows on disk, and persisted
 * beside them. Counts are plain integers; anything that depends on the clock is
 * kept as a SORTED array of timestamps and answered by binary search at read
 * time, so the row never goes stale at 04:00.
 *
 * Every field is defined over the card facts in `./facts.ts`; the counters in
 * `./counters.ts` are the only intended readers of the arrays.
 */
import { addDays } from 'date-fns/addDays';

import {
  adjustedEndOfDay,
  adjustedStartOfDay,
} from '../../algorithms/spaced-repetition/adjusted-time';
import { cardSchedulingFacts, type SummaryCard } from './facts';

/**
 * Bumped whenever a field is added to `DeckStudySummary`. A persisted row
 * written by an older version is recomputed at boot instead of trusted, so a
 * new field never reads as `undefined` from a row that predates it.
 */
export const DECK_STUDY_SUMMARY_VERSION = 2;

export interface DeckStudySummary {
  /** Rows in the bucket, tombstones and loading stubs included — the residency
   *  yardstick (`deckCardsResident` compares a bucket's size with it). Never
   *  compare it with `cardCount`, which counts VISIBLE cards. */
  rowCount: number;
  /** `filterVisibleStudyCards(cards).length` — the deck's displayed total. */
  visibleCount: number;
  /** Visible cards with no study history. */
  newCount: number;
  /** Visible, studied, leech-tagged — the `leech` label. */
  leechCount: number;
  /** Visible, studied, not a leech — the denominator `toReview` + `memorised`
   *  labels split. */
  withStateNonLeechCount: number;
  /** Of `withStateNonLeechCount`, cards whose last scheduling entry cannot be
   *  read. `getCardLabel` still calls them memorised; `isMemorisedCard` does
   *  not, so mastery subtracts them. */
  unreadableNonLeechCount: number;
  /** Visible cards that are starred. */
  starredCount: number;
  /** Rows — visible or not — ever answered good/easy: `countStudiedCards`
   *  (the explore "study 10 cards" detector) counted over the deck. */
  everGoodCount: number;
  /** Max studied instant over ALL rows (ms since epoch); 0 when never studied.
   *  Equals `deckLastStudiedAtMs(cards)`. */
  lastStudiedAtMs: number;
  /** Sorted ascending: due instants (ms) of visible, studied cards with a
   *  parseable `nextDueISO` — leeches INCLUDED. The to-do count reads this:
   *  `isCardDue` does not exclude leeches. */
  dueMsAll: number[];
  /** `dueMsAll` minus leeches — what the `toReview` label and mastery read. */
  dueMsNonLeech: number[];
  /** Sorted ascending: first-studied instants (ms) of visible, studied cards
   *  (see `CardSchedulingFacts.firstStudiedMs`) — the "new cards studied today"
   *  input. */
  firstStudiedMs: number[];
}

/**
 * Sort ascending IN PLACE through a `Float64Array`: the typed-array sort is
 * numeric without a JS comparator call per comparison (~2.5x faster on a 5k
 * deck, and these three sorts dominate a re-summary once the per-card facts
 * are cached). Same order as `sort((a, b) => a - b)` for these inputs: every
 * value is a finite `Date` instant (`-0` and `NaN` never occur, the only
 * values the two orders treat differently).
 */
function sortAscending(xs: number[]): void {
  if (xs.length < 2) return;
  const sorted = new Float64Array(xs).sort();
  for (let i = 0; i < sorted.length; i += 1) xs[i] = sorted[i];
}

export const EMPTY_DECK_STUDY_SUMMARY: DeckStudySummary = Object.freeze({
  rowCount: 0,
  visibleCount: 0,
  newCount: 0,
  leechCount: 0,
  withStateNonLeechCount: 0,
  unreadableNonLeechCount: 0,
  starredCount: 0,
  everGoodCount: 0,
  lastStudiedAtMs: 0,
  dueMsAll: Object.freeze([]) as unknown as number[],
  dueMsNonLeech: Object.freeze([]) as unknown as number[],
  firstStudiedMs: Object.freeze([]) as unknown as number[],
}) as DeckStudySummary;

/**
 * Summarise one deck's cards. Accepts the store's bucket shape
 * (`Record<cardId, card>`) or a plain array; only OWN enumerable entries of a
 * bucket are read. One pass, no intermediate card arrays.
 */
export function summarizeDeckCards(
  cards: readonly SummaryCard[] | Record<string, SummaryCard> | null | undefined,
): DeckStudySummary {
  if (!cards) return EMPTY_DECK_STUDY_SUMMARY;

  let rowCount = 0;
  let visibleCount = 0;
  let newCount = 0;
  let leechCount = 0;
  let withStateNonLeechCount = 0;
  let unreadableNonLeechCount = 0;
  let starredCount = 0;
  let everGoodCount = 0;
  let lastStudiedAtMs = 0;
  const dueMsAll: number[] = [];
  const dueMsNonLeech: number[] = [];
  const firstStudiedMs: number[] = [];

  const visit = (card: SummaryCard) => {
    rowCount += 1;
    const facts = cardSchedulingFacts(card);
    if (facts.lastStudiedMs > lastStudiedAtMs) lastStudiedAtMs = facts.lastStudiedMs;
    if (facts.everGood) everGoodCount += 1;
    if (!facts.visible) return;
    visibleCount += 1;
    if (facts.starred) starredCount += 1;
    if (!facts.hasState) {
      newCount += 1;
      return;
    }
    if (facts.dueMs !== null) dueMsAll.push(facts.dueMs);
    if (facts.firstStudiedMs !== null) firstStudiedMs.push(facts.firstStudiedMs);
    if (facts.leech) {
      leechCount += 1;
      return;
    }
    withStateNonLeechCount += 1;
    if (!facts.readableAlgoInfo) unreadableNonLeechCount += 1;
    if (facts.dueMs !== null) dueMsNonLeech.push(facts.dueMs);
  };

  if (Array.isArray(cards)) {
    for (const card of cards as readonly SummaryCard[]) visit(card);
  } else {
    const bucket = cards as Record<string, SummaryCard>;
    for (const id in bucket) {
      if (Object.prototype.hasOwnProperty.call(bucket, id)) visit(bucket[id]);
    }
  }

  if (rowCount === 0) return EMPTY_DECK_STUDY_SUMMARY;

  sortAscending(dueMsAll);
  sortAscending(dueMsNonLeech);
  sortAscending(firstStudiedMs);

  return {
    rowCount,
    visibleCount,
    newCount,
    leechCount,
    withStateNonLeechCount,
    unreadableNonLeechCount,
    starredCount,
    everGoodCount,
    lastStudiedAtMs,
    dueMsAll,
    dueMsNonLeech,
    firstStudiedMs,
  };
}

/**
 * `summary` after every card of the deck lost its study history (a progress
 * reset): the same rows, all visible cards new. Exact — no card is needed —
 * which is what lets a deck whose bodies are not on this device show the reset.
 */
export function resetDeckStudySummary<S extends DeckStudySummary>(summary: S): S {
  return {
    ...summary,
    newCount: summary.visibleCount,
    leechCount: 0,
    withStateNonLeechCount: 0,
    unreadableNonLeechCount: 0,
    everGoodCount: 0,
    lastStudiedAtMs: 0,
    dueMsAll: [],
    dueMsNonLeech: [],
    firstStudiedMs: [],
  };
}

/** Number of entries of a SORTED ascending array strictly below `cutoff` —
 *  the strict `<` of `isBefore`. */
export function countBelow(sorted: readonly number[], cutoff: number): number {
  let lo = 0;
  let hi = sorted.length;
  while (lo < hi) {
    const mid = (lo + hi) >>> 1;
    if (sorted[mid] < cutoff) lo = mid + 1;
    else hi = mid;
  }
  return lo;
}

/** Entries of a SORTED ascending array in `[fromInclusive, toExclusive)`. */
export function countInRange(
  sorted: readonly number[],
  fromInclusive: number,
  toExclusive: number,
): number {
  if (toExclusive <= fromInclusive) return 0;
  return countBelow(sorted, toExclusive) - countBelow(sorted, fromInclusive);
}

/** `isCardDue(card, endOfDay)` counted over the deck: due instants strictly
 *  before `endOfDay`, leeches included. */
export function dueCount(summary: DeckStudySummary, endOfDay: Date | number): number {
  return countBelow(
    summary.dueMsAll,
    typeof endOfDay === 'number' ? endOfDay : endOfDay.getTime(),
  );
}

/**
 * Cards of the deck first studied "today" — `isCardFirstStudiedToday` counted
 * over the deck, where today is the adjusted day (`cutoffTime`) that `now`
 * falls in.
 *
 * `isAdjustedSameDay(first, now)` holds when `now` lies inside FIRST's adjusted
 * day, `[start(first), start(first) + 1 day − 1 s]`. Solved for `first` given
 * `now`, that is every `first` in `[start(now), start(now) + 1 day)` — except
 * during the final sub-second of the day (`now` past `adjustedEndOfDay(now)`),
 * when the closed interval admits nothing but `first === now` itself. Both
 * branches are reproduced so the two counts agree at every millisecond.
 */
export function newStudiedTodayCount(
  summary: DeckStudySummary,
  now: Date,
  cutoffTime: { hours: number; minutes?: number },
): number {
  const nowMs = now.getTime();
  if (Number.isNaN(nowMs) || summary.firstStudiedMs.length === 0) return 0;
  const end = adjustedEndOfDay(now, cutoffTime).getTime();
  if (nowMs > end) {
    return countInRange(summary.firstStudiedMs, nowMs, nowMs + 1);
  }
  const start = adjustedStartOfDay(now, cutoffTime);
  return countInRange(
    summary.firstStudiedMs,
    start.getTime(),
    addDays(start, 1).getTime(),
  );
}
